import { createContext, useEffect, useMemo, useState } from "react";

import {
	enUS as coreEnUS,
	nbNO as coreNbNO,
	ptBR as corePtBR,
} from "@mui/material/locale";
import { type Theme, alpha, createTheme, darken } from "@mui/material/styles";
import { enUS, nbNO, ptBR } from "@mui/x-data-grid/locales";
import {
	enUS as pickersEnUS,
	nbNO as pickersNbNO,
	ptBR as pickersPtBR,
} from "@mui/x-date-pickers/locales";

import { useFetchActiveUser } from "@/service/ApplicationQueries";

// color design tokens export
export type Mode = "light" | "dark";

/** Theme variant IDs for the appearance dialog (single-theme grid and light/dark cards). */
export type ThemeVariantId =
	| "light-default"
	| "light-protanopia-deuteranopia"
	| "light-tritanopia"
	| "dark-default"
	| "dark-protanopia-deuteranopia"
	| "dark-tritanopia"
	| "soft-dark";

/** Resolve palette mode from a theme variant (light-* => light, dark-* and soft-dark => dark). */
export const getModeFromVariant = (variant: ThemeVariantId): Mode =>
	variant.startsWith("light") ? "light" : "dark";

/** Text size options for the appearance dialog. */
export type TextSizeId = "small" | "normal" | "large" | "extra-large";

/** Corner radius scale for surfaces and controls. */
export type CornerRadiusId = "compact" | "balanced" | "rounded";

/** Layout spacing density for controls and panels. */
export type DensityId = "compact" | "comfortable" | "relaxed";

/** Border intensity for cards, inputs, and overlays. */
export type BorderStyleId = "subtle" | "standard" | "strong";

/** Surface border tone for local component styling built on top of theme divider tokens. */
export type SurfaceBorderTone = "subtle" | "default" | "strong";

/** Motion intensity for transitions and animations. */
export type MotionId = "full" | "reduced" | "none";

/** Typography scale multiplier per text size (base = 14px). */
export const TEXT_SIZE_SCALE: Record<TextSizeId, number> = {
	small: 0.875,
	normal: 1,
	large: 1.125,
	"extra-large": 1.25,
};

const SHAPE_RADIUS_SCALE: Record<CornerRadiusId, number> = {
	compact: 2,
	balanced: 3,
	rounded: 4,
};

const STORAGE_KEYS = {
	mode: "colorMode",
	syncWithSystem: "themeSyncWithSystem",
	lightVariant: "themeLightVariant",
	darkVariant: "themeDarkVariant",
	singleVariant: "themeSingleVariant",
	highContrast: "themeHighContrast",
	textSize: "themeTextSize",
	cornerRadius: "themeCornerRadius",
	density: "themeDensity",
	borderStyle: "themeBorderStyle",
	motion: "themeMotion",
} as const;

type ColorTokens = {
	[key: string]: {
		[key: number]: string;
		main?: string;
	};
};

export const tokens = (mode: Mode): ColorTokens => {
	const inverted = (colors: Record<string, string | undefined>) => {
		const keys = Object.keys(colors);
		return keys.reduce((acc: { [key: string]: string }, key) => {
			const invertedValue = colors[keys[keys.length - 1 - keys.indexOf(key)]];
			acc[key] = invertedValue ?? "";
			return acc;
		}, {});
	};

	const baseTokens: ColorTokens = {
		// Core neutrals (primary text use)
		grey: {
			100: "#FFFFFF", // Iceberg White
			200: "#F5F5F5",
			300: "#E0E0E0",
			400: "#BDBDBD",
			500: "#9E9E9E",
			600: "#757575",
			700: "#2A2A2A", // Warm Gray
			800: "#121212",
			900: "#000000", // Eerie Black
		},
		// Calm Blue as primary brand color
		primary: {
			100: "#E3F0FF",
			200: "#BBD7FF",
			300: "#93BDFF",
			400: "#6AA3FF",
			500: "#1F80FF",
			600: "#1762C2",
			700: "#124A94",
			800: "#0C3266",
			900: "#071A38",
		},
		// Medium Green (success)
		greenAccent: {
			main: "#4CAF50",
			100: "#E8F5E9",
			200: "#C8E6C9",
			300: "#A5D6A7",
			400: "#81C784",
			500: "#4CAF50",
			600: "#43A047",
			700: "#388E3C",
			800: "#2E7D32",
			900: "#1B5E20",
		},
		// Bright Red (error)
		redAccent: {
			100: "#FBE3E0",
			200: "#F5B7AF",
			300: "#EF8A7E",
			400: "#E95E4D",
			500: "#D61202",
			600: "#B40F02",
			700: "#920C01",
			800: "#6F0901",
			900: "#4D0600",
		},
		// Calm Blue accent scale
		blueAccent: {
			100: "#E3F0FF",
			200: "#BBD7FF",
			300: "#93BDFF",
			400: "#6AA3FF",
			500: "#1F80FF",
			600: "#1762C2",
			700: "#124A94",
			800: "#0C3266",
			900: "#071A38",
		},
		// Amber Orange - warning / highlight
		yellowAccent: {
			100: "#FFF4E0",
			200: "#FFE3B3",
			300: "#FFCE80",
			400: "#FFB24D",
			500: "#C56A00",
			600: "#A94E00",
			700: "#7D3A00",
			800: "#522600",
			900: "#2E1500",
		},
		// Muted Teal – additional accent
		tealAccent: {
			100: "#E0FBFD",
			200: "#B3F4F8",
			300: "#80EDF3",
			400: "#4DE6EE",
			500: "#00D6E0",
			600: "#00AAB3",
			700: "#008186",
			800: "#00595A",
			900: "#00302F",
		},
	};

	return mode === "dark"
		? baseTokens
		: Object.fromEntries(
				Object.entries(baseTokens).map(([key, value]) => [
					key,
					inverted(value),
				]),
			);
};

const getSurfaceBorderWidthValue = (
	highContrast: boolean,
	borderStyle: BorderStyleId,
): number => Math.max(highContrast ? 2 : 1, borderStyle === "strong" ? 2 : 1);

const tuneDividerForBorderStyle = (
	divider: string,
	mode: Mode,
	highContrast: boolean,
	borderStyle: BorderStyleId,
): string => {
	if (highContrast) return divider;
	if (borderStyle === "subtle") {
		return alpha(divider, mode === "dark" ? 0.28 : 0.55);
	}
	if (borderStyle === "strong") {
		return alpha(divider, mode === "dark" ? 0.68 : 0.98);
	}
	return divider;
};

export const getSurfaceBorderColor = (
	theme: Theme,
	tone: SurfaceBorderTone = "default",
): string => {
	if (tone === "default" || isHighContrastTheme(theme)) {
		return theme.palette.divider;
	}

	if (tone === "strong") {
		return alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.82 : 0.96);
	}

	return alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.56 : 0.72);
};

export const getSurfaceBorderStyle = (
	theme: Theme,
	tone: SurfaceBorderTone = "default",
): string => `${getSurfaceBorderWidth(theme)} solid ${getSurfaceBorderColor(theme, tone)}`;

export const getCardBorderColor = (theme: Theme): string =>
	getSurfaceBorderColor(theme);

export const isHighContrastTheme = (theme: Theme): boolean =>
	["#5B6B80", "#B8C4D4", "#475569", "#CBD5E1"].includes(theme.palette.divider);

export const getSurfaceBorderWidth = (theme?: Theme): string => {
	void theme;
	return "var(--app-surface-border-width, 1px)";
};

export const getCardBorderStyle = (theme: Theme): string =>
	getSurfaceBorderStyle(theme);

/** Variant-specific palette overrides (accessibility + soft-dark). Applied on top of base palette. */
function getVariantPaletteOverrides(
	variant: ThemeVariantId,
	mode: Mode,
	highContrast: boolean,
): Partial<{
	primary: { main: string };
	secondary: { main: string };
	info: { main: string };
	success: { main: string };
	error: { main: string };
	warning: { main: string };
	background: { default: string; paper: string };
	text: { primary: string; secondary: string; disabled: string };
	divider: string;
	action: {
		hover: string;
		selected: string;
		focus: string;
		disabledBackground: string;
	};
}> {
	// Light variants
	if (variant === "light-protanopia-deuteranopia") {
		return {
			primary: { main: "#2563EB" },
			secondary: { main: "#0F766E" },
			info: { main: "#2563EB" },
			success: { main: "#2563EB" }, // blue instead of green
			error: { main: "#B45309" }, // amber/brown
			warning: { main: "#B45309" },
			background: { default: "#F4F7FC", paper: "#FFFFFF" },
			text: {
				primary: "#0F172A",
				secondary: highContrast ? "#334155" : "#52637A",
				disabled: highContrast ? "#64748B" : "#8A9AAF",
			},
			...(highContrast ? {} : { divider: alpha("#C7D2E0", 0.9) }),
			action: {
				hover: "rgba(82, 99, 122, 0.08)",
				selected: "rgba(37, 99, 235, 0.10)",
				focus: "rgba(37, 99, 235, 0.18)",
				disabledBackground: "rgba(148, 163, 184, 0.08)",
			},
		};
	}
	if (variant === "light-tritanopia") {
		return {
			primary: { main: "#0F766E" },
			secondary: { main: "#0EA5E9" },
			info: { main: "#0F766E" },
			success: { main: "#0F766E" },
			error: { main: "#C2410C" },
			warning: { main: "#C2410C" },
			background: { default: "#F4FBFA", paper: "#FFFFFF" },
			text: {
				primary: "#102A2A",
				secondary: highContrast ? "#244444" : "#4F6B6B",
				disabled: highContrast ? "#4F6B6B" : "#8AA2A2",
			},
			...(highContrast ? {} : { divider: alpha("#BFD9D6", 0.9) }),
			action: {
				hover: "rgba(79, 107, 107, 0.08)",
				selected: "rgba(15, 118, 110, 0.10)",
				focus: "rgba(15, 118, 110, 0.18)",
				disabledBackground: "rgba(138, 162, 162, 0.10)",
			},
		};
	}
	// Dark variants
	if (variant === "dark-protanopia-deuteranopia") {
		return {
			primary: { main: "#60A5FA" },
			secondary: { main: "#2DD4BF" },
			info: { main: "#60A5FA" },
			success: { main: "#60A5FA" },
			error: { main: "#F59E0B" },
			warning: { main: "#F59E0B" },
			background: { default: "#0A1325", paper: "#111C32" },
			text: {
				primary: "#E8EEF8",
				secondary: highContrast ? "#D1D9E8" : "#A9B8D0",
				disabled: highContrast ? "#A9B8D0" : "#7E90AD",
			},
			...(highContrast ? {} : { divider: alpha("#5B6C86", 0.55) }),
			action: {
				hover: "rgba(169, 184, 208, 0.08)",
				selected: "rgba(96, 165, 250, 0.18)",
				focus: "rgba(96, 165, 250, 0.28)",
				disabledBackground: "rgba(169, 184, 208, 0.08)",
			},
		};
	}
	if (variant === "dark-tritanopia") {
		return {
			primary: { main: "#2DD4BF" },
			secondary: { main: "#A78BFA" },
			info: { main: "#2DD4BF" },
			success: { main: "#2DD4BF" },
			error: { main: "#FB7185" },
			warning: { main: "#FB7185" },
			background: { default: "#081919", paper: "#102424" },
			text: {
				primary: "#E7FAF8",
				secondary: highContrast ? "#BEE8E3" : "#9DD3CE",
				disabled: highContrast ? "#9DD3CE" : "#6FA6A1",
			},
			...(highContrast ? {} : { divider: alpha("#3F6A66", 0.62) }),
			action: {
				hover: "rgba(157, 211, 206, 0.08)",
				selected: "rgba(45, 212, 191, 0.16)",
				focus: "rgba(45, 212, 191, 0.26)",
				disabledBackground: "rgba(111, 166, 161, 0.10)",
			},
		};
	}
	if (variant === "soft-dark" && mode === "dark") {
		return {
			primary: { main: "#7CC4FF" },
			secondary: { main: "#7CE7D6" },
			info: { main: "#7CC4FF" },
			background: { default: "#151A22", paper: "#1D2532" },
			text: {
				primary: "#E6EBF5",
				secondary: highContrast ? "#CDD7E6" : "#A6B1C2",
				disabled: highContrast ? "#A6B1C2" : "#748197",
			},
			...(highContrast ? {} : { divider: alpha("#52607A", 0.58) }),
			action: {
				hover: "rgba(166, 177, 194, 0.08)",
				selected: "rgba(124, 196, 255, 0.14)",
				focus: "rgba(124, 196, 255, 0.24)",
				disabledBackground: "rgba(116, 129, 151, 0.10)",
			},
		};
	}
	return {};
}

// mui theme settings; highContrast strengthens text and divider visibility; variant applies color overrides; textSize scales typography
export const themeSettings = (
	mode: Mode,
	highContrast = false,
	variant?: ThemeVariantId,
	textSize: TextSizeId = "normal",
	cornerRadius: CornerRadiusId = "balanced",
	density: DensityId = "comfortable",
	borderStyle: BorderStyleId = "standard",
	motion: MotionId = "full",
): object => {
	const scale = TEXT_SIZE_SCALE[textSize];
	const colors = tokens(mode);
	const variantOverrides = variant
		? getVariantPaletteOverrides(variant, mode, highContrast)
		: {};
	const fontFamily = [
		"Inter",
		"-apple-system",
		"BlinkMacSystemFont",
		'"Segoe UI"',
		"sans-serif",
	].join(",");
	const shapeRadius = SHAPE_RADIUS_SCALE[cornerRadius];
	const borderRadius = shapeRadius * 2;
	const dialogSurfaceRadius = shapeRadius * 3;
	const densityTokens = {
		compact: {
			buttonMinHeight: 34,
			buttonPadding: "6px 12px",
			inputMinHeight: 36,
			inputPadding: "8px 10px",
			tabMinHeight: 44,
			tabPadding: "10px 14px",
			chipHeight: 22,
			dialogTitlePadding: "12px 14px",
			dialogContentPadding: "14px",
			dialogActionsPadding: "10px 14px",
			tableCellPadding: "8px 10px",
			dataGridHeaderPadding: "0 10px",
			dataGridToolbarPadding: "6px 10px",
			menuItemMinHeight: 32,
		},
		comfortable: {
			buttonMinHeight: 38,
			buttonPadding: "8px 14px",
			inputMinHeight: 40,
			inputPadding: "10px 12px",
			tabMinHeight: 48,
			tabPadding: "12px 16px",
			chipHeight: 24,
			dialogTitlePadding: "14px 16px",
			dialogContentPadding: "16px",
			dialogActionsPadding: "12px 16px",
			tableCellPadding: "10px 12px",
			dataGridHeaderPadding: "0 12px",
			dataGridToolbarPadding: "8px 12px",
			menuItemMinHeight: 34,
		},
		relaxed: {
			buttonMinHeight: 42,
			buttonPadding: "10px 16px",
			inputMinHeight: 44,
			inputPadding: "12px 14px",
			tabMinHeight: 52,
			tabPadding: "14px 18px",
			chipHeight: 28,
			dialogTitlePadding: "16px 18px",
			dialogContentPadding: "18px",
			dialogActionsPadding: "14px 18px",
			tableCellPadding: "12px 14px",
			dataGridHeaderPadding: "0 14px",
			dataGridToolbarPadding: "10px 14px",
			menuItemMinHeight: 38,
		},
	}[density];
	const motionTokens = {
		full: {
			short: 200,
			standard: 260,
			complex: 340,
			colorTransition:
				"border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
		},
		reduced: {
			short: 120,
			standard: 160,
			complex: 220,
			colorTransition:
				"border-color 0.12s ease, box-shadow 0.12s ease, background-color 0.12s ease",
		},
		none: {
			short: 0,
			standard: 0,
			complex: 0,
			colorTransition: "none",
		},
	}[motion];
	const overlayShadow =
		mode === "dark"
			? "0 10px 24px rgba(0, 0, 0, 0.30)"
			: "0 8px 20px rgba(15, 23, 42, 0.08)";
	const focusRing =
		mode === "dark"
			? "0 0 0 3px rgba(31, 128, 255, 0.18)"
			: "0 0 0 3px rgba(31, 128, 255, 0.12)";
	const surfaceBorderWidth = getSurfaceBorderWidthValue(
		highContrast,
		borderStyle,
	);
	const scrollbarThumb =
		mode === "dark" ? alpha("#E2E8F0", 0.18) : alpha("#0F172A", 0.15);
	const scrollbarThumbHover =
		mode === "dark" ? alpha("#E2E8F0", 0.28) : alpha("#0F172A", 0.24);
	const paletteBase =
		mode === "dark"
			? {
					primary: { main: colors.primary[500] },
					secondary: { main: colors.tealAccent[500] },
					info: { main: colors.blueAccent[400] },
					error: { main: colors.redAccent[400] },
					success: { main: colors.greenAccent[400] },
					warning: { main: colors.yellowAccent[400] },
					neutral: {
						dark: "#E2E8F0",
						main: "#94A3B8",
						light: "#F8FAFC",
					},
					text: {
						primary: "#E6EDF7",
						secondary: highContrast ? "#CBD5E1" : "#9FB0C7",
						disabled: highContrast ? "#94A3B8" : "#6F829C",
					},
					background: { default: "#0B1220", paper: "#0F172A" },
					divider: highContrast ? "#5B6B80" : alpha("#64748B", 0.42),
					action: {
						hover: "rgba(148, 163, 184, 0.09)",
						selected: "rgba(31, 128, 255, 0.16)",
						focus: "rgba(31, 128, 255, 0.24)",
						disabledBackground: "rgba(148, 163, 184, 0.10)",
					},
				}
			: {
					primary: { main: colors.primary[500] },
					secondary: { main: colors.tealAccent[500] },
					info: { main: colors.blueAccent[500] },
					error: { main: colors.redAccent[500] },
					success: { main: colors.greenAccent[500] },
					warning: { main: colors.yellowAccent[400] },
					neutral: {
						dark: "#475569",
						main: "#94A3B8",
						light: "#F8FAFC",
					},
					text: {
						primary: "#0F172A",
						secondary: highContrast ? "#334155" : "#52637A",
						disabled: highContrast ? "#64748B" : "#8A9AAF",
					},
					background: { default: "#F3F7FB", paper: "#FFFFFF" },
					divider: highContrast ? "#B8C4D4" : alpha("#C7D2E0", 0.8),
					action: {
						hover: "rgba(100, 116, 139, 0.08)",
						selected: "rgba(31, 128, 255, 0.08)",
						focus: "rgba(31, 128, 255, 0.18)",
						disabledBackground: "rgba(148, 163, 184, 0.10)",
					},
				};
	const palette = {
		...paletteBase,
		...variantOverrides,
		divider: tuneDividerForBorderStyle(
			variantOverrides.divider ?? paletteBase.divider,
			mode,
			highContrast,
			borderStyle,
		),
	};
	const getHighEmphasisFill = (
		theme: Theme,
		colorKey: "primary" | "success" | "info" | "warning" | "error",
	) => {
		const paletteColor = theme.palette[colorKey];
		const backgroundColor =
			theme.palette.mode === "light" ? paletteColor.dark : paletteColor.main;
		const color =
			theme.palette.mode === "dark" && colorKey !== "warning"
				? theme.palette.common.white
				: theme.palette.getContrastText(backgroundColor);

		return {
			backgroundColor,
			color,
			borderColor: alpha(backgroundColor, 0.28),
		};
	};
	const getOutlinedActionStyles = (
		theme: Theme,
		options?: {
			textColor?: string;
			hoverBorderColor?: string;
			hoverBackgroundColor?: string;
			hoverTextColor?: string;
		},
	) => ({
		backgroundColor: theme.palette.background.paper,
		borderColor: theme.palette.divider,
		borderWidth: surfaceBorderWidth,
		color: options?.textColor ?? theme.palette.text.primary,
		boxShadow: "none",
		"&:hover": {
			borderColor:
				options?.hoverBorderColor ??
				alpha(theme.palette.text.secondary, theme.palette.mode === "dark" ? 0.6 : 0.45),
			backgroundColor:
				options?.hoverBackgroundColor ?? theme.palette.action.hover,
			color: options?.hoverTextColor ?? options?.textColor ?? theme.palette.text.primary,
			boxShadow: "none",
		},
	});
	const getPrimarySelectionFill = (theme: Theme) =>
		getHighEmphasisFill(theme, "primary");
	return {
		overrides: {
			MuiStepIcon: {
				root: {
					"&$completed": {
						color: "pink",
					},
					"&$active": {
						color: "red",
					},
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					root: {
						".even-row": {
							backgroundColor: "transparent",
						},
					},
				},
			},
		},
		shape: {
			borderRadius: shapeRadius,
		},
		palette: {
			contrastThreshold: 4.5,
			mode: mode,
			common: {
				black: "#000000",
				white: "#FFFFFF",
			},
			...palette,
		},
		transitions: {
			duration: {
				shortest: motionTokens.short,
				shorter: motionTokens.short,
				short: motionTokens.standard,
				standard: motionTokens.standard,
				complex: motionTokens.complex,
				enteringScreen: motionTokens.standard,
				leavingScreen: motionTokens.short,
			},
		},
		typography: {
			fontFamily,
			fontSize: Math.round(14 * scale),
			fontWeightRegular: 400,
			fontWeightMedium: 500,
			fontWeightSemiBold: 600,
			fontWeightBold: 700,
			h1: {
				fontFamily,
				fontSize: Math.round(32 * scale),
				fontWeight: 600,
				lineHeight: 1.2,
			},
			h2: {
				fontFamily,
				fontSize: Math.round(28 * scale),
				fontWeight: 600,
				lineHeight: 1.25,
			},
			h3: {
				fontFamily,
				fontSize: Math.round(24 * scale),
				fontWeight: 600,
				lineHeight: 1.25,
			},
			h4: {
				fontFamily,
				fontSize: Math.round(20 * scale),
				fontWeight: 600,
				lineHeight: 1.3,
			},
			h5: {
				fontFamily,
				fontSize: Math.round(18 * scale),
				fontWeight: 600,
				lineHeight: 1.35,
			},
			h6: {
				fontFamily,
				fontSize: Math.round(16 * scale),
				fontWeight: 600,
				lineHeight: 1.35,
			},
			body1: { fontFamily, fontSize: Math.round(14 * scale), lineHeight: 1.5 },
			body2: { fontFamily, fontSize: Math.round(13 * scale), lineHeight: 1.45 },
			subtitle1: {
				fontFamily,
				fontSize: Math.round(16 * scale),
				fontWeight: 500,
				lineHeight: 1.45,
			},
			subtitle2: {
				fontFamily,
				fontSize: Math.round(14 * scale),
				fontWeight: 500,
				lineHeight: 1.4,
			},
			caption: {
				fontFamily,
				fontSize: Math.round(12 * scale),
				lineHeight: 1.4,
			},
			button: {
				fontFamily,
				fontSize: Math.round(14 * scale),
				fontWeight: 600,
				textTransform: "none",
			},
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: (theme: Theme) => ({
					"*, *::before, *::after": {
						boxSizing: "border-box",
						borderColor: theme.palette.divider,
						scrollbarWidth: "thin",
						scrollbarColor: `${scrollbarThumb} transparent`,
						...(motion === "none"
							? {
									transition: "none !important",
									animation: "none !important",
								}
							: {}),
					},
					":root": {
						"--app-surface-border-width": `${surfaceBorderWidth}px`,
						"--app-surface-radius": `${borderRadius}px`,
					},
					"*::-webkit-scrollbar": {
						width: 6,
						height: 6,
					},
					"*::-webkit-scrollbar-track": {
						background: "transparent",
					},
					"*::-webkit-scrollbar-thumb": {
						background: scrollbarThumb,
						borderRadius: 999,
					},
					"*::-webkit-scrollbar-thumb:hover": {
						background: scrollbarThumbHover,
					},
					html: {
						backgroundColor: theme.palette.background.default,
						fontSize: `${Math.round(14 * scale)}px`,
						WebkitFontSmoothing: "antialiased",
						MozOsxFontSmoothing: "grayscale",
						scrollBehavior: motion === "full" ? "smooth" : "auto",
						"input[type='date'], input[type='time'], input[type='datetime-local']":
							{
								colorScheme: mode,
							},
					},
					body: {
						margin: 0,
						backgroundColor: theme.palette.background.default,
						color: theme.palette.text.primary,
						fontSize: Math.round(14 * scale),
						fontFamily,
					},
					a: {
						color: theme.palette.primary.main,
						textDecoration: "none",
						"&:hover": {
							textDecoration: "underline",
						},
					},
					":focus-visible": {
						outline: `2px solid ${theme.palette.primary.main}`,
						outlineOffset: 2,
					},
				}),
			},
			MuiPaper: {
				defaultProps: {
					elevation: 0,
				},
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						backgroundColor: theme.palette.background.paper,
						backgroundImage: "none",
						border: `${surfaceBorderWidth}px solid ${getCardBorderColor(theme)}`,
						borderRadius,
						boxShadow: "none",
						"&:before": {
							backgroundColor: "transparent !important",
						},
					}),
					outlined: ({ theme }: { theme: Theme }) => ({
						borderWidth: surfaceBorderWidth,
						borderColor: getCardBorderColor(theme),
					}),
				},
			},
			MuiCard: {
				defaultProps: {
					elevation: 0,
				},
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						backgroundColor: theme.palette.background.paper,
						border: `${surfaceBorderWidth}px solid ${getCardBorderColor(theme)}`,
						borderRadius,
						boxShadow: "none",
					}),
				},
			},
			MuiSwitch: {
				defaultProps: {
					disableRipple: true,
					size: "small",
				},
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => {
						const offBackground =
							theme.palette.mode === "dark"
								? alpha(theme.palette.primary.light, 0.18)
								: alpha(theme.palette.primary.main, 0.08);
						const offBorder =
							theme.palette.mode === "dark"
								? alpha(theme.palette.common.white, 0.2)
								: alpha(theme.palette.primary.main, 0.16);
						const thumbShadow =
							theme.palette.mode === "dark"
								? "0 6px 14px rgba(0, 0, 0, 0.28)"
								: "0 2px 4px rgba(15, 23, 42, 0.12)";
						const ornamentColor =
							theme.palette.mode === "dark"
								? alpha(theme.palette.common.white, 0.74)
								: alpha(theme.palette.text.secondary, 0.72);
						const checkedColor =
							theme.palette.mode === "dark"
								? darken(theme.palette.primary.main, 0.08)
								: theme.palette.primary.main;

						return {
							width: 54,
							height: 30,
							padding: 0,
							overflow: "visible",
							display: "inline-flex",
							alignItems: "center",
							"--satos-switch-track-width": "54px",
							"--satos-switch-track-height": "30px",
							"--satos-switch-thumb-width": "23px",
							"--satos-switch-thumb-height": "21px",
							"--satos-switch-thumb-radius": "5px",
							"--satos-switch-padding": "4px",
							"--satos-switch-translate-x":
								"calc(var(--satos-switch-track-width) - var(--satos-switch-thumb-width) - (var(--satos-switch-padding) * 2))",
							"--satos-switch-ornament-size": "9px",
							"--satos-switch-ornament-gap": "11px",
							"--satos-switch-line-height": "11px",
							"--satos-switch-off-bg": offBackground,
							"--satos-switch-off-border": offBorder,
							"--satos-switch-off-ornament": ornamentColor,
							"--satos-switch-thumb-shadow": thumbShadow,
							"--satos-switch-thumb-border": alpha(
								theme.palette.common.white,
								theme.palette.mode === "dark" ? 0.12 : 0.02,
							),
							"--satos-switch-checked-bg": checkedColor,
							"--satos-switch-focus-ring": alpha(
								theme.palette.primary.main,
								theme.palette.mode === "dark" ? 0.38 : 0.22,
							),
							"& .MuiSwitch-switchBase": {
								padding: "var(--satos-switch-padding)",
								color: "#FFFFFF",
								transition:
									"transform 200ms ease, color 200ms ease, opacity 200ms ease",
								"&:hover": {
									backgroundColor: "transparent",
								},
							},
							"& .MuiSwitch-switchBase.Mui-focusVisible + .MuiSwitch-track": {
								boxShadow:
									"0 0 0 4px var(--satos-switch-focus-ring), 0 0 0 1px var(--satos-switch-checked-bg)",
							},
							"& .MuiSwitch-switchBase.Mui-checked": {
								transform: "translateX(var(--satos-switch-translate-x))",
								color: "#FFFFFF",
							},
							"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
								backgroundColor:
									"var(--satos-switch-checked-bg) !important",
								borderColor:
									"var(--satos-switch-checked-bg) !important",
								opacity: 1,
							},
							"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track::before":
								{
									opacity: 1,
									transform: "translateY(-50%) scaleY(1)",
								},
							"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track::after": {
								opacity: 0,
								transform: "translateY(-50%) scale(0.75)",
							},
							"& .MuiSwitch-switchBase.Mui-disabled": {
								opacity: 0.56,
							},
							"& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
								opacity: 0.72,
							},
							"& .MuiSwitch-thumb": {
								width: "var(--satos-switch-thumb-width)",
								height: "var(--satos-switch-thumb-height)",
								borderRadius: "var(--satos-switch-thumb-radius)",
								backgroundColor: "#FFFFFF",
								boxShadow:
									"inset 0 0 0 1px var(--satos-switch-thumb-border), var(--satos-switch-thumb-shadow)",
							},
							"& .MuiSwitch-track": {
								width: "var(--satos-switch-track-width)",
								height: "var(--satos-switch-track-height)",
								borderRadius: "9px",
								backgroundColor: "var(--satos-switch-off-bg)",
								border: "1px solid var(--satos-switch-off-border)",
								boxSizing: "border-box",
								opacity: 1,
								position: "relative",
								transition: motionTokens.colorTransition,
								"&::before": {
									content: '""',
									position: "absolute",
									top: "50%",
									left: "var(--satos-switch-ornament-gap)",
									width: 2,
									height: "var(--satos-switch-line-height)",
									borderRadius: 999,
									backgroundColor: alpha("#FFFFFF", 0.94),
									opacity: 0,
									transform: "translateY(-50%) scaleY(0.7)",
									transition: "inherit",
								},
								"&::after": {
									content: '""',
									position: "absolute",
									top: "50%",
									right: "var(--satos-switch-ornament-gap)",
									width: "var(--satos-switch-ornament-size)",
									height: "var(--satos-switch-ornament-size)",
									borderRadius: "50%",
									border: "2px solid var(--satos-switch-off-ornament)",
									boxSizing: "border-box",
									transform: "translateY(-50%) scale(1)",
									transition: "inherit",
								},
							},
						};
					},
					sizeSmall: {
						width: 50,
						height: 28,
						"--satos-switch-track-width": "50px",
						"--satos-switch-track-height": "28px",
						"--satos-switch-thumb-width": "21px",
						"--satos-switch-thumb-height": "19px",
						"--satos-switch-thumb-radius": "5px",
						"--satos-switch-ornament-size": "8px",
						"--satos-switch-ornament-gap": "10px",
						"--satos-switch-line-height": "10px",
					},
				},
			},
			MuiDivider: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						borderColor: theme.palette.divider,
						opacity: 1,
					}),
				},
			},
			MuiDrawer: {
				styleOverrides: {
					paper: ({ theme }: { theme: Theme }) => ({
						backgroundColor: theme.palette.background.paper,
						color: theme.palette.text.primary,
						boxShadow: "none",
						border: "none",
						borderRight: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
						"&.MuiDrawer-paperAnchorRight": {
							borderRight: "none",
							borderLeft: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
						},
						"&.MuiDrawer-paperAnchorTop": {
							borderRight: "none",
							borderBottom: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
						},
						"&.MuiDrawer-paperAnchorBottom": {
							borderRight: "none",
							borderTop: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
						},
						padding: "0px 12px",
					}),
					root: ({ theme }: { theme: Theme }) => ({
						"& .Mui-selected": {
							backgroundColor: theme.palette.action.selected,
							color: theme.palette.primary.main,
							borderRadius,
							"&:hover": {
								backgroundColor: theme.palette.action.selected,
							},
						},
						"& .MuiListItemIcon-root": {
							color: "inherit",
						},
					}),
				},
			},
			MuiListItemButton: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						borderRadius,
						margin: "2px 0",
						"&.Mui-selected": {
							backgroundColor: theme.palette.action.selected,
							color: theme.palette.primary.main,
							"& .MuiListItemIcon-root": {
								color: theme.palette.primary.main,
							},
						},
						"&.Mui-selected:hover": {
							backgroundColor: theme.palette.action.selected,
						},
					}),
				},
			},
			MuiAccordionSummary: {
				styleOverrides: {
					content: {
						overflow: "hidden",
					},
				},
			},
			MuiStepIcon: {
				styleOverrides: {
					root: {
						"&.Mui-active": {
							color: colors.blueAccent[400],
							"&.Mui-error": {
								color: colors.redAccent[400],
							},
						},
						"&.Mui-completed": {
							color: colors.greenAccent[400],
						},
					},
				},
			},
			MuiButton: {
				defaultProps: {
					disableElevation: true,
				},
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						minHeight: densityTokens.buttonMinHeight,
						borderRadius,
						padding: densityTokens.buttonPadding,
						fontWeight: 600,
						boxShadow: "none",
						textTransform: "none",
						transition: motionTokens.colorTransition,
						"&:focus-visible": {
							outline: `2px solid ${theme.palette.primary.main}`,
							outlineOffset: 2,
							boxShadow: focusRing,
						},
					}),
					containedPrimary: ({ theme }: { theme: Theme }) => {
						const { backgroundColor, color } = getHighEmphasisFill(
							theme,
							"primary",
						);
						const hoverBackgroundColor = darken(backgroundColor, 0.08);

						return {
							backgroundColor,
							color,
							boxShadow: "none",
							"&:hover": {
								backgroundColor: hoverBackgroundColor,
								color,
								boxShadow: "none",
							},
						};
					},
					containedSuccess: ({ theme }: { theme: Theme }) => {
						const { backgroundColor, color } = getHighEmphasisFill(
							theme,
							"success",
						);
						const hoverBackgroundColor = darken(backgroundColor, 0.08);

						return {
							backgroundColor,
							color,
							boxShadow: "none",
							"&:hover": {
								backgroundColor: hoverBackgroundColor,
								color,
								boxShadow: "none",
							},
						};
					},
					containedInfo: ({ theme }: { theme: Theme }) => {
						const { backgroundColor, color } = getHighEmphasisFill(theme, "info");
						const hoverBackgroundColor = darken(backgroundColor, 0.08);

						return {
							backgroundColor,
							color,
							boxShadow: "none",
							"&:hover": {
								backgroundColor: hoverBackgroundColor,
								color,
								boxShadow: "none",
							},
						};
					},
					containedWarning: ({ theme }: { theme: Theme }) => {
						const { backgroundColor, color } = getHighEmphasisFill(
							theme,
							"warning",
						);
						const hoverBackgroundColor = darken(backgroundColor, 0.08);

						return {
							backgroundColor,
							color,
							boxShadow: "none",
							"&:hover": {
								backgroundColor: hoverBackgroundColor,
								color,
								boxShadow: "none",
							},
						};
					},
					containedError: ({ theme }: { theme: Theme }) => {
						const { backgroundColor, color } = getHighEmphasisFill(theme, "error");
						const hoverBackgroundColor = darken(backgroundColor, 0.08);

						return {
							backgroundColor,
							color,
							boxShadow: "none",
							"&:hover": {
								backgroundColor: hoverBackgroundColor,
								color,
								boxShadow: "none",
							},
						};
					},
					outlined: ({ theme }: { theme: Theme }) => ({
						backgroundColor: theme.palette.background.paper,
						borderColor: theme.palette.divider,
						borderWidth: surfaceBorderWidth,
						boxShadow: "none",
						"&:hover": {
							borderColor: alpha(
								theme.palette.text.secondary,
								theme.palette.mode === "dark" ? 0.6 : 0.45,
							),
							backgroundColor: theme.palette.action.hover,
							boxShadow: "none",
						},
					}),
					outlinedPrimary: ({ theme }: { theme: Theme }) => ({
						...getOutlinedActionStyles(theme, {
							hoverBorderColor: theme.palette.primary.main,
							hoverBackgroundColor: alpha(
								theme.palette.primary.main,
								theme.palette.mode === "dark" ? 0.16 : 0.06,
							),
							hoverTextColor: theme.palette.primary.main,
						}),
					}),
					outlinedWarning: ({ theme }: { theme: Theme }) => ({
						...getOutlinedActionStyles(theme),
					}),
					outlinedInherit: ({ theme }: { theme: Theme }) => ({
						...getOutlinedActionStyles(theme),
					}),
					text: ({ theme }: { theme: Theme }) => ({
						color: theme.palette.primary.main,
						"&:hover": {
							backgroundColor: theme.palette.action.hover,
						},
					}),
				},
			},
			MuiTextField: {
				defaultProps: {
					variant: "outlined",
					size: "small",
				},
			},
			MuiFormControl: {
				defaultProps: {
					size: "small",
					variant: "outlined",
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						color: theme.palette.text.secondary,
						fontWeight: 500,
						"&.Mui-focused": {
							color: theme.palette.primary.main,
						},
					}),
				},
			},
			MuiInputBase: {
				styleOverrides: {
					root: {
						borderRadius,
					},
					input: ({ theme }: { theme: Theme }) => ({
						"&::placeholder": {
							color: theme.palette.text.secondary,
							opacity: 1,
						},
					}),
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						minHeight: densityTokens.inputMinHeight,
						borderRadius,
						backgroundColor: theme.palette.background.paper,
						transition: motionTokens.colorTransition,
						"& .MuiOutlinedInput-notchedOutline": {
							borderColor: theme.palette.divider,
							borderWidth: surfaceBorderWidth,
						},
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: alpha(
								theme.palette.text.secondary,
								theme.palette.mode === "dark" ? 0.6 : 0.45,
							),
						},
						"&.Mui-focused": {
							boxShadow: focusRing,
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: theme.palette.primary.main,
							borderWidth: surfaceBorderWidth,
						},
					}),
					input: {
						padding: densityTokens.inputPadding,
					},
				},
			},
			MuiSelect: {
				styleOverrides: {
					select: {
						paddingTop: 10,
						paddingBottom: 10,
						paddingLeft: 12,
						minHeight: "unset",
						display: "flex",
						alignItems: "center",
					},
					icon: ({ theme }: { theme: Theme }) => ({
						color: theme.palette.text.secondary,
					}),
				},
			},
			MuiFormHelperText: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						marginLeft: 2,
						marginRight: 2,
						color: theme.palette.text.secondary,
					}),
				},
			},

			MuiTabs: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						borderBottom: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
						minHeight: densityTokens.tabMinHeight,
						"& .MuiTabs-indicator": {
							backgroundColor: theme.palette.primary.main,
							height: 2,
							borderRadius: "2px 2px 0 0",
						},
						"& .MuiTabs-scrollButtons": {
							color: theme.palette.primary.main,
						},
					}),
				},
			},
			MuiTab: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						color: theme.palette.text.secondary,
						textTransform: "none",
						fontWeight: theme.typography?.fontWeightMedium ?? 500,
						minHeight: densityTokens.tabMinHeight,
						minWidth: "auto",
						padding: densityTokens.tabPadding,
						transition: motionTokens.colorTransition,
						"&.Mui-selected": {
							color: theme.palette.primary.main,
							fontWeight: 600,
						},
						"&:hover": {
							color: theme.palette.text.primary,
						},
						"&.Mui-focusVisible": {
							outline: `2px solid ${theme.palette.primary.main}`,
							outlineOffset: 2,
							boxShadow: focusRing,
						},
					}),
				},
			},
			MuiCheckbox: {
				styleOverrides: {
					root: {
						color: mode === "dark" ? colors.grey[100] : colors.grey[100],
						"&.Mui-checked": {
							color:
								mode === "dark" ? colors.greenAccent[400] : colors.primary[500],
						},
					},
				},
			},
			MuiTypography: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						color: theme.palette.text.primary,
					}),
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => {
						const headerBackground = alpha(
							theme.palette.primary.main,
							theme.palette.mode === "dark" ? 0.14 : 0.06,
						);
						const stripedRowBackground = alpha(
							theme.palette.primary.main,
							theme.palette.mode === "dark" ? 0.035 : 0.02,
						);
						const headerAccentBorderWidth = Math.max(surfaceBorderWidth, 2);
						const accentIconColor = alpha(
							theme.palette.text.secondary,
							theme.palette.mode === "dark" ? 0.7 : 0.55,
						);

						return {
							border: "none",
							backgroundColor: theme.palette.background.paper,
							color: theme.palette.text.primary,
							"& .MuiDataGrid-columnHeaders": {
								backgroundColor: headerBackground,
								borderBottom: `${headerAccentBorderWidth}px solid ${theme.palette.divider}`,
							},
							"& .MuiDataGrid-columnHeader": {
								padding: densityTokens.dataGridHeaderPadding,
							},
							"& .MuiDataGrid-columnHeaderTitle": {
								fontWeight: 700,
								color: theme.palette.text.primary,
								letterSpacing: "0.01em",
							},
							"& .MuiDataGrid-iconSeparator, & .MuiDataGrid-columnSeparator": {
								color: accentIconColor,
							},
							"& .MuiDataGrid-menuIconButton, & .MuiDataGrid-sortIcon": {
								color: accentIconColor,
							},
							"& .MuiDataGrid-cell": {
								padding: densityTokens.dataGridHeaderPadding,
								borderBottom: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
								color: theme.palette.text.primary,
							},
							"& .MuiDataGrid-row": {
								backgroundColor: "transparent",
								"&:hover": {
									backgroundColor: theme.palette.action.hover,
								},
								"&.Mui-selected": {
									backgroundColor: theme.palette.action.selected,
								},
								"&.Mui-selected:hover": {
									backgroundColor: theme.palette.action.selected,
								},
							},
							"& .MuiDataGrid-row:nth-of-type(even):not(.Mui-selected)": {
								backgroundColor: stripedRowBackground,
							},
							"& .MuiDataGrid-footerContainer": {
								borderTop: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
							},
							"& .MuiDataGrid-toolbarContainer": {
								padding: densityTokens.dataGridToolbarPadding,
								borderBottom: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
								backgroundColor: theme.palette.background.paper,
							},
							"& .MuiDataGrid-withBorderColor": {
								borderColor: theme.palette.divider,
							},
						};
					},
				},
			},
			MuiChip: {
				defaultProps: {
					size: "small",
				},
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						height: densityTokens.chipHeight,
						borderRadius,
						border: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
						backgroundColor:
							theme.palette.mode === "dark"
								? "rgba(148, 163, 184, 0.06)"
								: "#F8FAFC",
						color: theme.palette.text.secondary,
						fontWeight: 500,
					}),
					label: {
						paddingLeft: 8,
						paddingRight: 8,
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: ({ theme }: { theme: Theme }) => ({
						minWidth: 320,
						backgroundColor: theme.palette.background.paper,
						border: `${surfaceBorderWidth}px solid ${alpha(theme.palette.divider, 0.9)}`,
						borderRadius: dialogSurfaceRadius,
						boxShadow:
							theme.palette.mode === "dark"
								? "0 28px 90px rgba(0, 0, 0, 0.42)"
								: "0 28px 90px rgba(0, 0, 0, 0.18)",
						backgroundImage: "none",
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}),
				},
			},
			MuiDialogTitle: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						position: "relative",
						padding: densityTokens.dialogTitlePadding,
						paddingRight: "3.5rem",
						borderBottom: `${surfaceBorderWidth}px solid ${alpha(theme.palette.divider, 0.9)}`,
						fontSize: Math.round(16 * scale),
						fontWeight: 600,
						lineHeight: 1.35,
						color: theme.palette.text.primary,
						backgroundColor:
							theme.palette.mode === "dark"
								? alpha(theme.palette.common.white, 0.02)
								: alpha(theme.palette.primary.main, 0.02),
						"& .MuiIconButton-root[aria-label*='Close'], & .MuiIconButton-root[aria-label*='close']":
							{
								position: "absolute",
								right: 8,
								top: 8,
								color: theme.palette.text.secondary,
								"&:hover": {
									color: theme.palette.text.primary,
									backgroundColor: theme.palette.action.hover,
								},
							},
					}),
				},
			},
			MuiDialogContent: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						padding: densityTokens.dialogContentPadding,
						color: theme.palette.text.primary,
						backgroundColor: theme.palette.background.paper,
						backgroundImage: "none",
						"&.MuiDialogContent-dividers": {
							borderTop: `${surfaceBorderWidth}px solid ${alpha(theme.palette.divider, 0.9)}`,
							borderBottom: `${surfaceBorderWidth}px solid ${alpha(theme.palette.divider, 0.9)}`,
						},
					}),
				},
			},
			MuiDialogActions: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						padding: densityTokens.dialogActionsPadding,
						borderTop: `${surfaceBorderWidth}px solid ${alpha(theme.palette.divider, 0.9)}`,
						gap: 8,
						backgroundColor:
							theme.palette.mode === "dark"
								? alpha(theme.palette.common.white, 0.02)
								: alpha(theme.palette.primary.main, 0.02),
					}),
				},
			},
			MuiSnackbarContent: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						backgroundColor: theme.palette.background.paper,
						color: theme.palette.text.primary,
						border: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
						borderRadius,
						boxShadow: overlayShadow,
						backgroundImage: "none",
					}),
					message: {
						fontWeight: 500,
					},
				},
			},
			MuiAlert: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						alignItems: "center",
						borderRadius,
						border: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
						boxShadow: overlayShadow,
						backgroundImage: "none",
						fontWeight: 500,
						"& .MuiAlert-icon": {
							alignItems: "center",
							fontSize: Math.round(20 * scale),
							marginRight: 10,
							padding: "6px 0",
							opacity: 1,
						},
						"& .MuiAlert-message": {
							padding: "8px 0",
						},
						"& .MuiAlert-action": {
							alignItems: "center",
							marginRight: -4,
							paddingTop: 4,
							paddingBottom: 4,
						},
					}),
					standardSuccess: ({ theme }: { theme: Theme }) => ({
						backgroundColor: alpha(
							theme.palette.success.main,
							theme.palette.mode === "dark" ? 0.18 : 0.12,
						),
						color: theme.palette.text.primary,
						borderColor: alpha(theme.palette.success.main, 0.28),
					}),
					standardInfo: ({ theme }: { theme: Theme }) => ({
						backgroundColor: alpha(
							theme.palette.info.main,
							theme.palette.mode === "dark" ? 0.18 : 0.1,
						),
						color: theme.palette.text.primary,
						borderColor: alpha(theme.palette.info.main, 0.24),
					}),
					standardWarning: ({ theme }: { theme: Theme }) => ({
						backgroundColor: alpha(
							theme.palette.warning.main,
							theme.palette.mode === "dark" ? 0.18 : 0.12,
						),
						color: theme.palette.text.primary,
						borderColor: alpha(theme.palette.warning.main, 0.26),
					}),
					standardError: ({ theme }: { theme: Theme }) => ({
						backgroundColor: alpha(
							theme.palette.error.main,
							theme.palette.mode === "dark" ? 0.18 : 0.1,
						),
						color: theme.palette.text.primary,
						borderColor: alpha(theme.palette.error.main, 0.24),
					}),
					filledSuccess: ({ theme }: { theme: Theme }) => ({
						...getHighEmphasisFill(theme, "success"),
					}),
					filledInfo: ({ theme }: { theme: Theme }) => ({
						...getHighEmphasisFill(theme, "info"),
					}),
					filledWarning: ({ theme }: { theme: Theme }) => ({
						...getHighEmphasisFill(theme, "warning"),
					}),
					filledError: ({ theme }: { theme: Theme }) => ({
						...getHighEmphasisFill(theme, "error"),
					}),
				},
			},
			MuiButtonGroup: {
				styleOverrides: {
					root: {
						borderRadius,
						boxShadow: "none",
					},
					grouped: ({ theme }: { theme: Theme }) => ({
						boxShadow: "none",
						"&:not(:last-of-type)": {
							borderColor: theme.palette.divider,
						},
					}),
				},
			},
			MuiToggleButtonGroup: {
				styleOverrides: {
					root: {
						borderRadius,
					},
					grouped: ({ theme }: { theme: Theme }) => ({
						borderRadius,
						borderColor: theme.palette.divider,
						"&:not(:first-of-type)": {
							marginLeft: -surfaceBorderWidth,
						},
					}),
				},
			},
			MuiToggleButton: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => {
						const { backgroundColor, color, borderColor } =
							getPrimarySelectionFill(theme);
						const hoverBackgroundColor = darken(backgroundColor, 0.08);

						return {
							minHeight: densityTokens.buttonMinHeight,
							padding: densityTokens.buttonPadding,
							borderRadius,
							borderColor: theme.palette.divider,
							backgroundColor: theme.palette.background.paper,
							color: theme.palette.text.secondary,
							fontWeight: 600,
							textTransform: "none",
							boxShadow: "none",
							transition: motionTokens.colorTransition,
							"&:hover": {
								borderColor: theme.palette.primary.main,
								backgroundColor: alpha(
									theme.palette.primary.main,
									theme.palette.mode === "dark" ? 0.16 : 0.06,
								),
								color: theme.palette.primary.main,
								boxShadow: "none",
							},
							"&.Mui-selected": {
								backgroundColor,
								color,
								borderColor,
								boxShadow: "none",
							},
							"&.Mui-selected:hover": {
								backgroundColor: hoverBackgroundColor,
								color: theme.palette.getContrastText(hoverBackgroundColor),
								borderColor: alpha(hoverBackgroundColor, 0.32),
								boxShadow: "none",
							},
						};
					},
				},
			},
			MuiBackdrop: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						backgroundColor:
							theme.palette.mode === "dark"
								? "rgba(2, 6, 23, 0.42)"
								: "rgba(15, 23, 42, 0.24)",
						backdropFilter: "blur(8px) saturate(140%)",
						WebkitBackdropFilter: "blur(8px) saturate(140%)",
					}),
				},
			},
			MuiPopover: {
				styleOverrides: {
					paper: ({ theme }: { theme: Theme }) => ({
						backgroundColor: theme.palette.background.paper,
						border: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
						borderRadius,
						boxShadow: overlayShadow,
						backgroundImage: "none",
					}),
				},
			},
			MuiMenu: {
				styleOverrides: {
					paper: ({ theme }: { theme: Theme }) => ({
						backgroundColor: theme.palette.background.paper,
						border: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
						borderRadius,
						boxShadow: overlayShadow,
						backgroundImage: "none",
						marginTop: 4,
					}),
					list: {
						padding: 4,
					},
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						borderRadius,
						minHeight: densityTokens.menuItemMinHeight,
						transition: motionTokens.colorTransition,
						"&.Mui-selected": {
							backgroundColor: theme.palette.action.selected,
						},
						"&.Mui-selected:hover": {
							backgroundColor: theme.palette.action.selected,
						},
					}),
				},
			},
			MuiIconButton: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						borderRadius,
						color: theme.palette.text.secondary,
						transition: motionTokens.colorTransition,
						"&[aria-label*='Close'], &[aria-label*='close']": {
							border: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
							backgroundColor: theme.palette.background.paper,
						},
						"&:hover": {
							backgroundColor: theme.palette.action.hover,
							color: theme.palette.text.primary,
						},
						"&.Mui-focusVisible": {
							outline: `2px solid ${theme.palette.primary.main}`,
							outlineOffset: 2,
							boxShadow: focusRing,
						},
					}),
				},
			},
			MuiTableCell: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						borderBottom: `${surfaceBorderWidth}px solid ${theme.palette.divider}`,
						padding: densityTokens.tableCellPadding,
					}),
					head: ({ theme }: { theme: Theme }) => ({
						backgroundColor:
							theme.palette.mode === "dark"
								? "rgba(148, 163, 184, 0.04)"
								: "#F8FAFC",
						color: theme.palette.text.secondary,
						fontWeight: 600,
					}),
				},
			},
			MuiTablePagination: {
				styleOverrides: {
					toolbar: ({ theme }: { theme: Theme }) => ({
						minHeight: densityTokens.inputMinHeight,
						paddingLeft: 0,
						paddingRight: 0,
						color: theme.palette.text.secondary,
					}),
					selectLabel: ({ theme }: { theme: Theme }) => ({
						color: theme.palette.text.secondary,
					}),
					displayedRows: ({ theme }: { theme: Theme }) => ({
						color: theme.palette.text.secondary,
					}),
					actions: {
						marginLeft: 8,
					},
				},
			},
		},
	};
};

export type ThemeModeOption = "single" | "sync";

export interface ColorModeContextValue {
	mode: Mode;
	toggleColorMode: () => void;
	setMode: (mode: Mode) => void;
	themeMode: ThemeModeOption;
	setThemeMode: (v: ThemeModeOption) => void;
	syncWithSystem: boolean;
	lightVariant: ThemeVariantId;
	darkVariant: ThemeVariantId;
	singleVariant: ThemeVariantId;
	setLightVariant: (v: ThemeVariantId) => void;
	setDarkVariant: (v: ThemeVariantId) => void;
	setSingleVariant: (v: ThemeVariantId) => void;
	highContrast: boolean;
	setHighContrast: (v: boolean) => void;
	textSize: TextSizeId;
	setTextSize: (v: TextSizeId) => void;
	cornerRadius: CornerRadiusId;
	setCornerRadius: (v: CornerRadiusId) => void;
	density: DensityId;
	setDensity: (v: DensityId) => void;
	borderStyle: BorderStyleId;
	setBorderStyle: (v: BorderStyleId) => void;
	motion: MotionId;
	setMotion: (v: MotionId) => void;
}

const defaultVariantLight: ThemeVariantId = "light-default";
const defaultVariantDark: ThemeVariantId = "dark-default";

function readStored<T>(
	key: string,
	parse: (s: string) => T | null,
	fallback: T,
): T {
	if (typeof window === "undefined") return fallback;
	const raw = window.localStorage.getItem(key);
	if (raw == null) return fallback;
	const parsed = parse(raw);
	return parsed ?? fallback;
}

function persist(key: string, value: string) {
	if (typeof window !== "undefined") window.localStorage.setItem(key, value);
}

// context for color mode
export const ColorModeContext = createContext<ColorModeContextValue>({
	mode: "light",
	toggleColorMode: () => {},
	setMode: () => {},
	themeMode: "single",
	setThemeMode: () => {},
	syncWithSystem: false,
	lightVariant: defaultVariantLight,
	darkVariant: defaultVariantDark,
	singleVariant: defaultVariantLight,
	setLightVariant: () => {},
	setDarkVariant: () => {},
	setSingleVariant: () => {},
	highContrast: false,
	setHighContrast: () => {},
	textSize: "normal",
	setTextSize: () => {},
	cornerRadius: "balanced",
	setCornerRadius: () => {},
	density: "comfortable",
	setDensity: () => {},
	borderStyle: "standard",
	setBorderStyle: () => {},
	motion: "full",
	setMotion: () => {},
});

const VALID_VARIANTS: ThemeVariantId[] = [
	"light-default",
	"light-protanopia-deuteranopia",
	"light-tritanopia",
	"dark-default",
	"dark-protanopia-deuteranopia",
	"dark-tritanopia",
	"soft-dark",
];

function parseVariant(s: string): ThemeVariantId | null {
	return VALID_VARIANTS.includes(s as ThemeVariantId)
		? (s as ThemeVariantId)
		: null;
}

const VALID_TEXT_SIZES: TextSizeId[] = [
	"small",
	"normal",
	"large",
	"extra-large",
];

function parseTextSize(s: string): TextSizeId | null {
	return VALID_TEXT_SIZES.includes(s as TextSizeId) ? (s as TextSizeId) : null;
}

const VALID_CORNER_RADII: CornerRadiusId[] = ["compact", "balanced", "rounded"];

function parseCornerRadius(s: string): CornerRadiusId | null {
	return VALID_CORNER_RADII.includes(s as CornerRadiusId)
		? (s as CornerRadiusId)
		: null;
}

const VALID_DENSITIES: DensityId[] = ["compact", "comfortable", "relaxed"];

function parseDensity(s: string): DensityId | null {
	return VALID_DENSITIES.includes(s as DensityId) ? (s as DensityId) : null;
}

const VALID_BORDER_STYLES: BorderStyleId[] = ["subtle", "standard", "strong"];

function parseBorderStyle(s: string): BorderStyleId | null {
	return VALID_BORDER_STYLES.includes(s as BorderStyleId)
		? (s as BorderStyleId)
		: null;
}

const VALID_MOTION_LEVELS: MotionId[] = ["full", "reduced", "none"];

function parseMotion(s: string): MotionId | null {
	return VALID_MOTION_LEVELS.includes(s as MotionId) ? (s as MotionId) : null;
}

export const useMode = () => {
	const [themeMode, setThemeModeState] = useState<ThemeModeOption>(() =>
		readStored(
			STORAGE_KEYS.syncWithSystem,
			(s) => (s === "true" ? "sync" : "single"),
			"single",
		),
	);
	const [syncWithSystem, setSyncWithSystem] = useState<boolean>(() =>
		readStored(STORAGE_KEYS.syncWithSystem, (s) => s === "true", false),
	);
	const [lightVariant, setLightVariantState] = useState<ThemeVariantId>(() =>
		readStored(STORAGE_KEYS.lightVariant, parseVariant, defaultVariantLight),
	);
	const [darkVariant, setDarkVariantState] = useState<ThemeVariantId>(() =>
		readStored(STORAGE_KEYS.darkVariant, parseVariant, defaultVariantDark),
	);
	const [singleVariant, setSingleVariantState] = useState<ThemeVariantId>(
		() => {
			const stored = readStored(STORAGE_KEYS.singleVariant, parseVariant, null);
			if (stored) return stored;
			// Backward compatibility: legacy colorMode "dark" | "light"
			const legacy = readStored(
				STORAGE_KEYS.mode,
				(s) => (s === "dark" || s === "light" ? s : null),
				null,
			);
			return legacy === "dark" ? defaultVariantDark : defaultVariantLight;
		},
	);
	const [highContrast, setHighContrastState] = useState<boolean>(() =>
		readStored(STORAGE_KEYS.highContrast, (s) => s === "true", false),
	);
	const [textSize, setTextSizeState] = useState<TextSizeId>(() =>
		readStored(STORAGE_KEYS.textSize, parseTextSize, "normal"),
	);
	const [cornerRadius, setCornerRadiusState] = useState<CornerRadiusId>(() =>
		readStored(STORAGE_KEYS.cornerRadius, parseCornerRadius, "balanced"),
	);
	const [density, setDensityState] = useState<DensityId>(() =>
		readStored(STORAGE_KEYS.density, parseDensity, "comfortable"),
	);
	const [borderStyle, setBorderStyleState] = useState<BorderStyleId>(() =>
		readStored(STORAGE_KEYS.borderStyle, parseBorderStyle, "standard"),
	);
	const [motion, setMotionState] = useState<MotionId>(() =>
		readStored(STORAGE_KEYS.motion, parseMotion, "full"),
	);
	const [systemPref, setSystemPref] = useState<Mode>(() => {
		if (typeof window === "undefined") return "light";
		const m = window.matchMedia("(prefers-color-scheme: dark)");
		return m.matches ? "dark" : "light";
	});
	const { data: user } = useFetchActiveUser(true);

	// Effective mode: sync => from light/dark variant by system pref; single => from singleVariant
	const effectiveVariant: ThemeVariantId = syncWithSystem
		? systemPref === "light"
			? lightVariant
			: darkVariant
		: singleVariant;
	const mode: Mode = getModeFromVariant(effectiveVariant);

	// Listen to system preference when sync is on
	useEffect(() => {
		if (typeof window === "undefined") return;
		const m = window.matchMedia("(prefers-color-scheme: dark)");
		const handler = () => setSystemPref(m.matches ? "dark" : "light");
		m.addEventListener("change", handler);
		return () => m.removeEventListener("change", handler);
	}, []);

	const setMode = (next: Mode) => {
		const variant = next === "light" ? "light-default" : "dark-default";
		setSingleVariantState(variant);
		persist(STORAGE_KEYS.singleVariant, variant);
		persist(STORAGE_KEYS.mode, next);
	};

	const toggleColorMode = () => {
		setMode(mode === "light" ? "dark" : "light");
	};

	const setThemeMode = (v: ThemeModeOption) => {
		setThemeModeState(v);
		setSyncWithSystem(v === "sync");
		persist(STORAGE_KEYS.syncWithSystem, v === "sync" ? "true" : "false");
	};

	const setLightVariant = (v: ThemeVariantId) => {
		setLightVariantState(v);
		persist(STORAGE_KEYS.lightVariant, v);
	};

	const setDarkVariant = (v: ThemeVariantId) => {
		setDarkVariantState(v);
		persist(STORAGE_KEYS.darkVariant, v);
	};

	const setSingleVariant = (v: ThemeVariantId) => {
		setSingleVariantState(v);
		persist(STORAGE_KEYS.singleVariant, v);
	};

	const setHighContrast = (v: boolean) => {
		setHighContrastState(v);
		persist(STORAGE_KEYS.highContrast, v ? "true" : "false");
	};

	const setTextSize = (v: TextSizeId) => {
		setTextSizeState(v);
		persist(STORAGE_KEYS.textSize, v);
	};

	const setCornerRadius = (v: CornerRadiusId) => {
		setCornerRadiusState(v);
		persist(STORAGE_KEYS.cornerRadius, v);
	};

	const setDensity = (v: DensityId) => {
		setDensityState(v);
		persist(STORAGE_KEYS.density, v);
	};

	const setBorderStyle = (v: BorderStyleId) => {
		setBorderStyleState(v);
		persist(STORAGE_KEYS.borderStyle, v);
	};

	const setMotion = (v: MotionId) => {
		setMotionState(v);
		persist(STORAGE_KEYS.motion, v);
	};

	const colorMode: ColorModeContextValue = useMemo(
		() => ({
			mode,
			toggleColorMode,
			setMode,
			themeMode,
			setThemeMode,
			syncWithSystem,
			lightVariant,
			darkVariant,
			singleVariant,
			setLightVariant,
			setDarkVariant,
			setSingleVariant,
			highContrast,
			setHighContrast,
			textSize,
			setTextSize,
			cornerRadius,
			setCornerRadius,
			density,
			setDensity,
			borderStyle,
			setBorderStyle,
			motion,
			setMotion,
		}),
		[
			mode,
			themeMode,
			syncWithSystem,
			lightVariant,
			darkVariant,
			singleVariant,
			highContrast,
			textSize,
			cornerRadius,
			density,
			borderStyle,
			motion,
		],
	);

	// Localization based on backend language settings
	const locale = useMemo(
		() => ({
			dataGrid:
				user?.Company?.Language.LanguageString === "no"
					? nbNO
					: user?.Company?.Language.LanguageString === "pt"
						? ptBR
						: enUS,
			datePickers:
				user?.Company?.Language.LanguageString === "no"
					? pickersNbNO
					: user?.Company?.Language.LanguageString === "pt"
						? pickersPtBR
						: pickersEnUS,
			charts:
				user?.Company?.Language.LanguageString === "no"
					? nbNO
					: user?.Company?.Language.LanguageString === "pt"
						? ptBR
						: enUS,
			core:
				user?.Company?.Language.LanguageString === "no"
					? coreNbNO
					: user?.Company?.Language.LanguageString === "pt"
						? corePtBR
						: coreEnUS,
		}),
		[user?.Company?.Language],
	);

	// Dynamic theme creation (mode, effectiveVariant, highContrast, textSize drive appearance)
	const theme = useMemo(
		() =>
			createTheme(
				themeSettings(
					mode,
					highContrast,
					effectiveVariant,
					textSize,
					cornerRadius,
					density,
					borderStyle,
					motion,
				),
				locale.dataGrid,
				locale.datePickers,
				locale.core,
				locale.charts,
			),
		[
			mode,
			highContrast,
			effectiveVariant,
			textSize,
			cornerRadius,
			density,
			borderStyle,
			motion,
			locale,
		],
	);

	return { theme, colorMode };
};
